import graphql from "babel-plugin-relay/macro";
import styles from "components/account/details/AccountDetails.module.scss";
import { accountMessages } from "domain/account/accountMessages";
import { AccountReadonlyAdditionalDetails_account } from "generatedQueries/AccountReadonlyAdditionalDetails_account.graphql";
import { AccountReadonlyAdditionalDetails_configuration } from "generatedQueries/AccountReadonlyAdditionalDetails_configuration.graphql";
import { AccountReadonlyAdditionalDetails_isSeniorCitizenLabel } from "generatedQueries/AccountReadonlyAdditionalDetails_isSeniorCitizenLabel.graphql";
import moment from "moment";
import React, { FC } from "react";
import { useIntl } from "react-intl";
import { createFragmentContainer } from "react-relay";
import { BooleanMessage } from "shared/components/BooleanMessage";
import { HideableContainer, HideableFields } from "shared/components/Hideable";
import { Message } from "shared/components/Message";
import { sharedMessages } from "shared/sharedMessages";

export interface IAccountReadonlyAdditionalDetails {
    isSelfIdentifyAsSeniorCitizenEnabled: boolean;
    account: AccountReadonlyAdditionalDetails_account;
    configuration: AccountReadonlyAdditionalDetails_configuration | null;
    isSeniorCitizenLabel: AccountReadonlyAdditionalDetails_isSeniorCitizenLabel | null;
}

const InternalAccountReadonlyAdditionalDetails: FC<IAccountReadonlyAdditionalDetails> = ({ account, configuration, isSelfIdentifyAsSeniorCitizenEnabled, isSeniorCitizenLabel }) => {
    const intl = useIntl();
    const seniorCitizenLabel = isSeniorCitizenLabel?.value
        ? intl.formatMessage(accountMessages.customSeniorCitizenLabel, { personType: isSeniorCitizenLabel?.value })
        : intl.formatMessage(accountMessages.seniorCitizenLabel);

    return (
        <section aria-labelledby="additionalDetailsHeading">
            <h4 className="sr-only" id="additionalDetailsHeading"><Message message={accountMessages.additionalDetailsLabel} /></h4>
            <dl className={styles.accountDescriptionList}>
                <HideableContainer field={HideableFields.driverLicence} configuration={configuration}>
                    <dt><Message message={accountMessages.driverLicenceLabel} /></dt>
                    <dd>{account.driverLicence}</dd>
                </HideableContainer>
                {
                    !isSelfIdentifyAsSeniorCitizenEnabled &&
                    <HideableContainer field={HideableFields.dateOfBirth} configuration={configuration}>
                        <dt><Message message={accountMessages.dateOfBirthLabel} /></dt>
                        <dd>{formatDate(account.dateOfBirth)}</dd>
                    </HideableContainer>
                }
                {
                    isSelfIdentifyAsSeniorCitizenEnabled &&
                    <>
                        <dt>{seniorCitizenLabel}</dt>
                        <dd>
                            <BooleanMessage
                                value={account.isSelfIdentifiedAsSeniorCitizen}
                                confirmTrueMessage={accountMessages.seniorCitizenYesLabel}
                                confirmFalseMessage={accountMessages.seniorCitizenNoLabel} />
                        </dd>
                    </>
                }
                <HideableContainer field={HideableFields.canDiscloseToPublic} configuration={configuration}>
                    <dt><Message message={accountMessages.allowedDisclosureGroupLabel} /></dt>
                    <dd>
                        <BooleanMessage
                            value={account.canDiscloseToPublic}
                            confirmTrueMessage={sharedMessages.confirmYesLabel}
                            confirmFalseMessage={sharedMessages.confirmNoLabel} />
                    </dd>
                </HideableContainer>
                <HideableContainer field={HideableFields.isSubscribedToShelterNewsletter} configuration={configuration}>
                    <dt><Message message={accountMessages.subscribeToShelterNewsletter} /></dt>
                    <dd>
                        <BooleanMessage
                            value={account.isSubscribedToShelterNewsletter}
                            confirmTrueMessage={sharedMessages.confirmYesLabel}
                            confirmFalseMessage={sharedMessages.confirmNoLabel} />
                    </dd>
                </HideableContainer>
            </dl>
        </section>
    );
};

export const AccountReadonlyAdditionalDetails = createFragmentContainer(InternalAccountReadonlyAdditionalDetails, {
    account: graphql`
        fragment AccountReadonlyAdditionalDetails_account on Account {
            canDiscloseToPublic
            dateOfBirth
            driverLicence
            isSubscribedToShelterNewsletter
            isSelfIdentifiedAsSeniorCitizen
        }`,
    configuration: graphql`
        fragment AccountReadonlyAdditionalDetails_configuration on Configuration {
            ...Hideable_configuration
        }`,
    isSeniorCitizenLabel: graphql`
        fragment AccountReadonlyAdditionalDetails_isSeniorCitizenLabel on SiteCustomisation {
            value
        }`,
});

function formatDate(value: string | null) {
    return value && moment(value).format("L");
}
